import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { getServersData } from '../../actions/dashboard'
import { useDispatch, useSelector } from 'react-redux'
import Loader from '../../components/Loader'
import Table from '../../components/Table'
import { IoIosCheckmark, IoIosClose, IoMdSearch } from "react-icons/io";
import { FaServer, FaFilter, FaClock } from "react-icons/fa";
import TableSearch from '../../components/TableSearch'
import moment from 'moment'
import cn from 'classnames'
import styles from './styles.module.scss'


const Servers = () => {

  const dispatch = useDispatch()
  const { servers, loading } = useSelector(state => state.dashboard)
  const [filteredData, setFilteredData] = useState(null)
  const [sortState, setSortState] = useState({ key: null, order: null })
  const [searchQuery, setSearchQuery] = useState('')
  const [showMo, setShowMo] = useState(false)
  const [showArchive, setShowArchive] = useState(false)
  const [openSearchKey, setOpenSearchKey] = useState(null)
  const [statusFilter, setStatusFilter] = useState(null) // null, 'active', 'errors'

  useEffect(() => {
    dispatch(getServersData())
  }, [])



  const handleFilter = useCallback((result, query) => {
    if (!query) {
      setFilteredData(null)
      return
    }
    setFilteredData(result || [])
  }, [])

  const handleSearchOpen = useCallback((searchKey) => {
    setOpenSearchKey(searchKey)
  }, [])

  // Фильтрация по поисковому запросу 
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value)
  }

  // Вспомогательная функция для проверки значения поля
  const isFieldTrue = useCallback((value) => {
    return value === true || value === 1 || value === '1' || value === 'true'
  }, [])

  // Данные для статистики (без фильтра по статусу)
  const dataForStats = useMemo(() => {
    let data = servers

    // Фильтр по поисковому запросу
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim()
      data = data.filter(s =>
        (s.name && s.name.toLowerCase().includes(query)) ||
        (s.hostglobal && s.hostglobal.toLowerCase().includes(query))
      )
    }

    // Фильтр по МО
    if (showMo) {
      data = data.filter(s => isFieldTrue(s.mo))
    }

    // Фильтр по Архив
    if (showArchive) {
      data = data.filter(s => isFieldTrue(s.archive))
    }

    // Исключаем архивные, если не показываем архив
    if (!showArchive) {
      data = data?.filter(el => !el.archive)
    }

    return data
  }, [servers, searchQuery, showMo, showArchive, isFieldTrue])

  // Применяем фильтры поиска и порта
  const filteredBySearch = useMemo(() => {
    let data = dataForStats

    // Фильтр по статусу (активные/ошибки)
    if (statusFilter === 'active') {
      data = data.filter(s => s.status)
    } else if (statusFilter === 'errors') {
      data = data.filter(s => !s.status)
    }

    // Сортировка первичного массива по алфавиту
    if (data && data.length > 0) {
      data = [...data].sort((a, b) => {
        const aName = String(a?.name || '').toLowerCase().trim()
        const bName = String(b?.name || '').toLowerCase().trim()
        return aName.localeCompare(bName, 'ru', { numeric: true })
      })
    }

    return data
  }, [dataForStats, statusFilter])

  const handleSort = useCallback((sortedData, sortKey, sortOrder) => {
    setSortState({ key: sortOrder ? sortKey : null, order: sortOrder })
  }, [])

  // Комбинируем фильтрацию и сортировку
  const dataToRender = React.useMemo(() => {
    // Используем данные, отфильтрованные по поиску , если нет фильтра из TableSearch
    let data = filteredData ?? filteredBySearch

    if (sortState.order && sortState.key) {
      // Если есть активная сортировка, применяем её к текущим данным
      const sorted = data && [...data]?.sort((a, b) => {
        const aValue = a?.[sortState.key]
        const bValue = b?.[sortState.key]

        if (aValue === null || aValue === undefined) return 1
        if (bValue === null || bValue === undefined) return -1

        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return sortState.order === 'asc' ? aValue - bValue : bValue - aValue
        }

        const aStr = String(aValue).toLowerCase()
        const bStr = String(bValue).toLowerCase()

        if (sortState.order === 'asc') {
          return aStr.localeCompare(bStr, 'ru', { numeric: true })
        } else {
          return bStr.localeCompare(aStr, 'ru', { numeric: true })
        }
      })
      return sorted
    }

    return data
  }, [filteredData, filteredBySearch, sortState])

  const displayData = showArchive ? dataToRender : dataToRender?.filter(el => !el.archive)

  // Статистика (рассчитывается на основе данных без фильтра по статусу)
  const stats = useMemo(() => {
    if (!dataForStats || dataForStats.length === 0) return { total: 0, active: 0, errors: 0, availability: 0 }
    const total = dataForStats?.length
    const active = dataForStats?.filter(s => s.status).length
    const errors = dataForStats?.filter(s => !s.status).length
    const availability = total > 0 ? Math.round((active / total) * 100) : 0
    return { total, active, errors, availability }
  }, [dataForStats])

  if (loading) return <Loader />;

  const formattedDate = (date) => date ? moment(date).format('DD.MM.YYYY HH:mm:ss') : '';

  const renderRow = (item) => {
    return (
      <tr key={item.id}>
        <td>
          <div className={styles.serverNameCell}>
            <FaServer size={20} className={styles.serverIcon} />
            <span>{item.name}</span>
          </div>
        </td>
        <td>{item.hostglobal || '-'}</td>
        <td>{item.id || '-'}</td>
        <td>{item.port || '-'}</td>
        <td>{item.db || '-'}</td>
        <td>  {item.version || '-'}</td>
        <td>{formattedDate(item.time_good)}</td>
        <td>{formattedDate(item.time_bad)}</td>
        <td>
          <span className={cn(styles.statusBadge, {
            [styles.statusError]: !item.status,
            [styles.statusSuccess]: item.status
          })}>
            {item.status ? (
              <>
                <IoIosCheckmark size={16} />
              </>
            ) : (
              <>
                <IoIosClose size={16} />
              </>
            )}
          </span>
        </td>
      </tr >
    )
  }

  return (
    <div className={styles.serversPage}>
      {/* Поиск и фильтры */}
      <div className={styles.searchSection}>
        <div className={styles.searchBar}>
          <IoMdSearch size={20} className={styles.searchIcon} />
          <input
            type="text"
            placeholder="Поиск по наименованию или IP адресу..."
            value={searchQuery}
            onChange={handleSearchChange}
            className={styles.searchInput}
          />
        </div>

        <div className={styles.checkboxFilters}>
          <label className={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={showMo}
              onChange={(e) => setShowMo(e.target.checked)}
              className={styles.checkbox}
            />
            <span>МО</span>
          </label>
          <label className={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={showArchive}
              onChange={(e) => setShowArchive(e.target.checked)}
              className={styles.checkbox}
            />
            <span>Архив</span>
          </label>
        </div>
      </div>

      {/* Статистические карточки */}
      <div className={styles.statsCards}>
        <div
          className={cn(styles.statCard, { [styles.statCardActive]: statusFilter === null })}
          onClick={() => setStatusFilter(null)}
          style={{ cursor: 'pointer' }}
        >
          <div className={styles.statIcon} style={{ backgroundColor: '#e3f2fd' }}>
            <FaServer size={24} color="#1976d2" />
          </div>
          <div className={styles.statContent}>
            <div className={styles.statLabel}>Всего</div>
            <div className={styles.statValue}>{stats.total}</div>
            <div className={styles.statDescription}>Серверов в системе</div>
          </div>
        </div>
        <div
          className={cn(styles.statCard, { [styles.statCardActive]: statusFilter === 'active' })}
          onClick={() => setStatusFilter(statusFilter === 'active' ? null : 'active')}
          style={{ cursor: 'pointer' }}
        >
          <div className={styles.statIcon} style={{ backgroundColor: '#e8f5e9' }}>
            <IoIosCheckmark size={24} color="#2e7d32" />
          </div>
          <div className={styles.statContent}>
            <div className={styles.statLabel}>Активные</div>
            <div className={styles.statValue}>{stats.active}</div>
            <div className={styles.statDescription}>Работают нормально</div>
          </div>
        </div>
        <div
          className={cn(styles.statCard, { [styles.statCardActive]: statusFilter === 'errors' })}
          onClick={() => setStatusFilter(statusFilter === 'errors' ? null : 'errors')}
          style={{ cursor: 'pointer' }}
        >
          <div className={styles.statIcon} style={{ backgroundColor: '#ffebee' }}>
            <IoIosClose size={24} color="#c62828" />
          </div>
          <div className={styles.statContent}>
            <div className={styles.statLabel}>Ошибки</div>
            <div className={styles.statValue}>{stats.errors}</div>
            <div className={styles.statDescription}>Требуют внимания</div>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ backgroundColor: '#fff3e0' }}>
            <FaClock size={24} color="#f57c00" />
          </div>
          <div className={styles.statContent}>
            <div className={styles.statLabel}>Доступность</div>
            <div className={styles.statValue}>{stats.availability}%</div>
            <div className={styles.statDescription}>Общая статистика</div>
          </div>
        </div>
      </div>

      {/* Таблица */}
      <div className={styles.tableSection}>
        <h3 className={styles.tableTitle}>Список серверов ({displayData?.length})</h3>
        <Table maxHeight='60vh' minHeight='400px' dataLoaded={!loading && servers?.length > 0}>
          <thead>
            <tr>
              <th>
                <div className={styles.headerCellWrapper}>
                  <div>НАИМЕНОВАНИЕ</div>
                  <div>
                    <TableSearch
                      data={filteredBySearch}
                      searchKey="name"
                      onFilter={handleFilter}
                      onSort={handleSort}
                      activeSortKey={sortState.key}
                      activeSortOrder={sortState.order}
                      onSearchOpen={handleSearchOpen}
                      openSearchKey={openSearchKey}
                    />
                  </div>
                </div>
              </th>
              <th>
                <div className={styles.headerCellWrapper}>
                  <div>ГЛОБАЛЬНЫЙ IP</div>
                  <div>
                    <TableSearch
                      data={filteredBySearch}
                      searchKey="hostglobal"
                      onFilter={handleFilter}
                      onSort={handleSort}
                      activeSortKey={sortState.key}
                      activeSortOrder={sortState.order}
                      onSearchOpen={handleSearchOpen}
                      openSearchKey={openSearchKey}
                    />
                  </div>
                </div>
              </th>
              <th>
                <div className={styles.headerCellWrapper}>
                  <div>ID</div>
                  <div>
                    <TableSearch
                      data={filteredBySearch}
                      searchKey="id"
                      onFilter={handleFilter}
                      onSort={handleSort}
                      activeSortKey={sortState.key}
                      activeSortOrder={sortState.order}
                      onSearchOpen={handleSearchOpen}
                      openSearchKey={openSearchKey}
                    />
                  </div>
                </div>
              </th>
              <th>
                <div className={styles.headerCellWrapper}>
                  <div>ПОРТ</div>
                  <div>
                    <TableSearch
                      data={filteredBySearch}
                      searchKey="port"
                      onFilter={handleFilter}
                      onSort={handleSort}
                      activeSortKey={sortState.key}
                      activeSortOrder={sortState.order}
                      onSearchOpen={handleSearchOpen}
                      openSearchKey={openSearchKey}
                    />
                  </div>
                </div>
              </th>
              <th>
                <div className={styles.headerCellWrapper}>
                  <div>БАЗА ДАННЫХ</div>
                  <div>
                    <TableSearch
                      data={filteredBySearch}
                      searchKey="db"
                      onFilter={handleFilter}
                      onSort={handleSort}
                      activeSortKey={sortState.key}
                      activeSortOrder={sortState.order}
                      onSearchOpen={handleSearchOpen}
                      openSearchKey={openSearchKey}
                    />
                  </div>
                </div>
              </th>
              <th>
                <div className={styles.headerCellWrapper}>
                  <div>Версия программы</div>
                  <div>
                    <TableSearch
                      data={filteredBySearch}
                      searchKey="version"
                      onFilter={handleFilter}
                      onSort={handleSort}
                      activeSortKey={sortState.key}
                      activeSortOrder={sortState.order}
                      onSearchOpen={handleSearchOpen}
                      openSearchKey={openSearchKey}
                    />
                  </div>
                </div>
              </th>
              <th>

                <div className={styles.headerCellWrapper}>
                  <div>Успешное подкл.</div>
                  <div>
                    <TableSearch
                      data={filteredBySearch}
                      searchKey="time_good"
                      onFilter={handleFilter}
                      onSort={handleSort}
                      activeSortKey={sortState.key}
                      activeSortOrder={sortState.order}
                      onSearchOpen={handleSearchOpen}
                      openSearchKey={openSearchKey}
                    />
                  </div>
                </div>
              </th>
              <th>
                <div className={styles.headerCellWrapper}>
                  <div>Неудачное подкл.</div>
                  <div>
                    <TableSearch
                      data={filteredBySearch}
                      searchKey="time_bad"
                      onFilter={handleFilter}
                      onSort={handleSort}
                      activeSortKey={sortState.key}
                      activeSortOrder={sortState.order}
                      onSearchOpen={handleSearchOpen}
                      openSearchKey={openSearchKey}
                    />
                  </div>
                </div>
              </th>
              <th>
                <div className={styles.headerCellWrapper}>
                  <div>LMS</div>
                  <div>
                    <TableSearch
                      data={filteredBySearch}
                      searchKey="status"
                      onFilter={handleFilter}
                      onSort={handleSort}
                      activeSortKey={sortState.key}
                      activeSortOrder={sortState.order}
                      withoutSearch
                      onSearchOpen={handleSearchOpen}
                      openSearchKey={openSearchKey}
                    />
                  </div>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {displayData?.length > 0 && displayData.map(renderRow)}
          </tbody>
        </Table>
        {(!displayData || displayData.length === 0) && (
          <div className={styles.emptyMessage}>
            Нет данных
          </div>
        )}
      </div>
    </div>
  )
}

export default Servers